import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

export const useVoiceRecorder = ({ onRecorded, maxSeconds = 90 }) => {
    const [isRecording, setIsRecording] = useState(false);
    const [level, setLevel] = useState(0);
    const [seconds, setSeconds] = useState(0);
    const refs = useRef({});
    const onRecordedRef = useRef(onRecorded);
    onRecordedRef.current = onRecorded;

    const cleanup = useCallback(() => {
        const { stream, ctx, raf, timer } = refs.current;
        stream?.getTracks().forEach((t) => t.stop());
        if (ctx && ctx.state !== 'closed') ctx.close();
        if (raf) cancelAnimationFrame(raf);
        if (timer) clearInterval(timer);
    }, []);

    const stop = useCallback(() => {
        const recorder = refs.current.recorder;
        if (recorder && recorder.state !== 'inactive') recorder.stop();
        setIsRecording(false);
        setLevel(0);
    }, []);

    const start = useCallback(async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const ctx = new (window.AudioContext || window.webkitAudioContext)();
            const analyser = ctx.createAnalyser();
            analyser.fftSize = 256;
            ctx.createMediaStreamSource(stream).connect(analyser);
            const data = new Uint8Array(analyser.frequencyBinCount);
            const recorder = new MediaRecorder(stream);
            const chunks = [];
            recorder.ondataavailable = (e) => e.data.size && chunks.push(e.data);
            recorder.onstop = () => {
                cleanup();
                if (!refs.current.cancelled) {
                    onRecordedRef.current(new Blob(chunks, { type: recorder.mimeType || 'audio/webm' }));
                }
            };
            const tick = () => {
                analyser.getByteFrequencyData(data);
                setLevel(data.reduce((a, b) => a + b, 0) / data.length / 255);
                refs.current.raf = requestAnimationFrame(tick);
            };
            refs.current = { stream, ctx, recorder, cancelled: false };
            recorder.start();
            tick();
            setSeconds(0);
            refs.current.timer = setInterval(() => setSeconds((s) => s + 1), 1000);
            setIsRecording(true);
        } catch (error) {
            toast.error('Microphone access is needed to answer. Please allow it in your browser and try again.');
        }
    }, [cleanup]);

    useEffect(() => {
        if (isRecording && seconds >= maxSeconds) stop();
    }, [isRecording, seconds, maxSeconds, stop]);

    useEffect(() => () => {
        refs.current.cancelled = true;
        const recorder = refs.current.recorder;
        if (recorder && recorder.state !== 'inactive') recorder.stop();
        cleanup();
    }, [cleanup]);

    return { isRecording, level, seconds, start, stop };
};
